import React, {useState, useRef} from 'react';
import {View, Text, TouchableOpacity, TextInput, Modal, TouchableWithoutFeedback} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import {styles, getZoneCardStyles} from './styles';
import I18n from 'i18n-js';
import {ensureZonePrefix, getEditableZoneName} from '../../utils/zoneUtils';
import useWidth from '../scanToCartScanning/useWidth';

const ZoneCard = ({
  zone,
  editingZoneId,
  onUpdateZoneName,
  onAddRange,
  onDeleteRange,
  onUpdateRangeValue,
  onSave
}) => {
  const screenWidth = useWidth();
  const responsive = getZoneCardStyles(screenWidth);
  const [isEditingName, setIsEditingName] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [iconPosition, setIconPosition] = useState({x: 0, y: 0, width: 0, centerX: 0});
  const iconRef = useRef(null);
  const nameInputRef = useRef(null);

  const getEditableName = () => {
    return getEditableZoneName(zone.zoneName || zone.name || '');
  };

  const handleNameChange = (newEditableName) => {
    const fullName = ensureZonePrefix(newEditableName);
    onUpdateZoneName(zone.id, fullName);
  };

  const handleToggleTooltip = () => {
    if (iconRef.current) {
      iconRef.current.measureInWindow((x, y, width, height) => {
        const centerX = x + width / 2;
        setIconPosition({x, y: y + height, width, centerX});
      });
    }
    setShowTooltip(!showTooltip);
  };

  const getTooltipLeft = () => {
    const iconCenterX = iconPosition.centerX || 0;
    const left = iconCenterX - responsive.tooltipWidth / 2;
    return Math.max(10, Math.min(left, screenWidth - responsive.tooltipWidth - 10));
  };

  const getTooltipTop = () => {
    return iconPosition.y + 10;
  };

  const getArrowLeft = () => {
    if (!iconPosition.centerX || !iconPosition.x) return '48%';
    const tooltipLeft = getTooltipLeft();
    const iconCenterX = iconPosition.centerX;
    const arrowOffset = iconCenterX - tooltipLeft;
    const percentage = (arrowOffset / responsive.tooltipWidth) * 100 - 3;
    return `${Math.max(10, Math.min(90, percentage))}%`;
  };

  const renderRange = (range, index) => {
    if (!range) return null;
    return (
    <View
      key={range.id || `range-${index}`}
      style={[styles.rangeRow, responsive.rangeRow]}>
      <TouchableOpacity
        testID={`deleteRangeButton-${index}`}
        style={styles.deleteButton}
        onPress={() => onDeleteRange(zone.id, index)}>
        <i className="icon-cancel" style={{fontSize: responsive.iconSizes.delete, color: '#ffff'}} />
      </TouchableOpacity>

      <View style={[styles.rangeInputsContainer, responsive.rangeInputsContainer]}>
        <View style={[styles.rangeInputWrapper, responsive.rangeInputWrapper]}>
          {index === 0 && (
            <Text style={[styles.rangeLabel, responsive.rangeLabel]}>
              {I18n.t('zoneSettings.rangeStartLabel')}
            </Text>
          )}
          <TextInput
            testID={`rangeStartInput-${index}`}
            style={[styles.rangeInput, responsive.rangeInput]}
            value={range.rangeStart}
            onChangeText={value =>
              onUpdateRangeValue(zone.id, index, 'rangeStart', value)
            }
            placeholder={I18n.t('zoneSettings.rangeStartPlaceholder')}
            placeholderTextColor="#999"
          />
        </View>

        <View style={[styles.rangeInputWrapper, responsive.rangeInputWrapper]}>
          {index === 0 && (
            <Text style={[styles.rangeLabel, responsive.rangeLabel]}>
              {I18n.t('zoneSettings.rangeEndLabel')}
            </Text>
          )}
          <TextInput
            testID={`rangeEndInput-${index}`}
            style={[styles.rangeInput, responsive.rangeInput]}
            value={range.rangeEnd}
            onChangeText={value =>
              onUpdateRangeValue(zone.id, index, 'rangeEnd', value)
            }
            placeholder={I18n.t('zoneSettings.rangeEndPlaceholder')}
            placeholderTextColor="#999"
          />
        </View>
      </View>
    </View>
    );
  };

  return (
    <View style={[styles.zoneContainer, responsive.zoneContainer]}>
      <View style={[styles.zoneHeader, responsive.zoneHeader]}>
        {isEditingName ? (
          <View style={{flexDirection: 'row', alignItems: 'center', flex: 1}}>
            <Text
              style={[
                styles.zoneTitle,
                responsive.zoneTitle,
                {marginRight: 4}
              ]}>
              Zone
            </Text>
            <TextInput
              ref={nameInputRef}
              testID="zoneNameInput"
              style={[
                styles.zoneTitleInput,
                responsive.zoneTitleInput
              ]}
              value={getEditableName()}
              onChangeText={handleNameChange}
              onBlur={() => setIsEditingName(false)}
              autoFocus
            />
          </View>
        ) : (
          <Text
            style={[styles.zoneTitle, responsive.zoneTitle]}>
            {zone.zoneName || zone.name || ''}
          </Text>
        )}

        <TouchableOpacity
          testID="editZoneNameButton"
          style={[styles.iconButton, responsive.iconButton]}
          onPress={() => setIsEditingName(true)}>
          <i className="icon-edit" style={{fontSize: responsive.iconSizes.edit, color: '#ffff'}} />
        </TouchableOpacity>
      </View>

      <View style={[styles.infoDescriptionContainer, responsive.infoDescriptionContainer]}>
        <Text style={[styles.infoDescriptionText, responsive.infoDescriptionText]}>
          {I18n.t('zoneSettings.infoTooltip')}
        </Text>
        <TouchableOpacity
          testID="zoneInfoButton"
          ref={iconRef}
          style={[styles.iconButton, responsive.iconButton]}
          onPress={handleToggleTooltip}>
          <i
            className="icon-info-circled"
            style={{fontSize: responsive.iconSizes.info, color: '#ffff'}}
          />
        </TouchableOpacity>
      </View>

      {zone.ranges
        .map((range, index) => ({ range, index }))
        .filter(({ range }) => !range._destroy)
        .map(({ range, index }) => renderRange(range, index))}

      {editingZoneId !== zone.id ? (
        <TouchableOpacity
          testID="addRangeButton"
          style={[styles.addRangeButton, responsive.addRangeButton]}
          onPress={() => onAddRange(zone.id)}>
          <i className="icon-plus" style={{color: '#ffff', fontSize: responsive.iconSizes.add}}></i>
          <Text
            style={[
              styles.addRangeText,
              responsive.addRangeText
            ]}>
            {I18n.t('zoneSettings.addRangeLabel')}
          </Text>
        </TouchableOpacity>
      ) : (
        <TouchableOpacity
          testID="saveZoneButton"
          style={[styles.addRangeButton, responsive.addRangeButton]}
          onPress={() => onSave(zone.id)}>
          <Text
            style={[
              styles.addRangeText,
              responsive.addRangeText
            ]}>
            {I18n.t('scanToCart.saveChanges')}
          </Text>
        </TouchableOpacity>
      )}

      <Modal
        transparent
        visible={showTooltip}
        onRequestClose={() => setShowTooltip(false)}>
        <TouchableWithoutFeedback
          testID="tooltipContainer"
          onPress={() => setShowTooltip(false)}>
          <View style={styles.modalOverlay}>
            <LinearGradient
              locations={[0, 1]}
              colors={['#8a9daf', '#d7e1e9']}
              style={[
                styles.tooltipContainer,
                  {
                    position: 'absolute',
                    top: getTooltipTop(),
                    left: getTooltipLeft()
                  },
                  responsive.tooltipContainer
              ]}>
              <View
                style={[
                  styles.tooltipArrow,
                  {
                    top: -10,
                    left: getArrowLeft()
                  }
                ]}
              />
              <Text style={[styles.tooltipText, responsive.tooltipText]}>
                {I18n.t('zoneSettings.editZoneTooltip')}
              </Text>
            </LinearGradient>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </View>
  );
};

export default ZoneCard;

